import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faShieldHalved } from '@fortawesome/pro-regular-svg-icons/faShieldHalved';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faStar } from '@fortawesome/pro-regular-svg-icons/faStar';
import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faUser as faUserSolid } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faShieldHalved as faShieldHalvedSolid } from '@fortawesome/pro-solid-svg-icons/faShieldHalved';
import { faCreditCard as faCreditCardSolid } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faBell as faBellSolid } from '@fortawesome/pro-solid-svg-icons/faBell';

export interface AccountSection {
  activeIcon: IconDefinition;
  group: 'account' | 'content';
  href: string;
  icon: IconDefinition;
  id: string;
  labelKey: string;
  label: string;
}

export const SECTION_GROUPS: Record<AccountSection['group'], { labelKey: string; label: string }> = {
  account: { label: 'Account', labelKey: 'Shell.group-account' },
  content: { label: 'Content & Alerts', labelKey: 'Shell.group-content' },
};

export const ACCOUNT_SECTIONS: AccountSection[] = [
  {
    activeIcon: faUserSolid,
    group: 'account',
    href: '/account/profile',
    icon: faUser,
    id: 'profile',
    label: 'Profile',
    labelKey: 'Profile.profile-info',
  },
  {
    activeIcon: faShieldHalvedSolid,
    group: 'account',
    href: '/account/security',
    icon: faShieldHalved,
    id: 'security',
    label: 'Security & Privacy',
    labelKey: 'Shell.security-privacy',
  },
  {
    activeIcon: faCreditCardSolid,
    group: 'account',
    href: '/account/billing',
    icon: faCreditCard,
    id: 'billing',
    label: 'Plans & Billing',
    // Fresh key on purpose: `Billing.label` translates to just "Billing".
    labelKey: 'Shell.plans-billing',
  },
  {
    activeIcon: faStarSolid,
    group: 'content',
    href: '/account/following',
    icon: faStar,
    id: 'following',
    label: 'Following',
    labelKey: 'Shell.following',
  },
  {
    activeIcon: faBellSolid,
    group: 'content',
    href: '/account/notifications',
    icon: faBell,
    id: 'notifications',
    label: 'Notifications & Alerts',
    labelKey: 'Shell.notifications-alerts',
  },
];

export const DEFAULT_SECTION_HREF = '/account/profile';

export const getAccountSection = (id: string): AccountSection | undefined =>
  ACCOUNT_SECTIONS.find(section => section.id === id);

export const sectionMetaTitle = (id: string): string => {
  const label = getAccountSection(id)?.label ?? 'Account';
  return `My Account ${label} Settings - Benzinga`;
};

export const FULL_PAGE_SEGMENTS = ['delete-account'];

/**
 * Legacy tab URLs → new section routes.
 *
 * `news-alerts` and `email-subscriptions` were the Option A interim tabs:
 * they wrapped the legacy components so the shell could ship before the
 * rebuilt tabs existed. Both are superseded by Notifications & Alerts
 * (#15876) and retired here, so their URLs — and the older
 * `watchlist-alerts` that used to point at them — land on the alert centre.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  chat: '/account/profile',
  'email-subscriptions': '/account/notifications',
  'news-alerts': '/account/notifications',
  'watchlist-alerts': '/account/notifications',
};
